import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../API';
import { useAuth } from '../providers/AuthContext';

export const BDSA_ACCESS_QUERY_KEY = 'bdsa-access';

/**
 * Whether the current user may use Data Manager surfaces (definitions + Case Builder).
 * Site admins always qualify; others need the Data Managers group (via GET /bdsa/access).
 *
 * Refetches on every mount and on window focus so group membership changes
 * (e.g. admin just added the user) apply without a full page reload.
 */
export function useDataManagerAccess() {
    const { user, token } = useAuth();
    const queryClient = useQueryClient();
    const enabled = !!token && !!user;

    const { data, isLoading, isFetching, refetch } = useQuery({
        queryKey: [BDSA_ACCESS_QUERY_KEY, user?._id],
        queryFn: () => api.get('bdsa/access'),
        enabled,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
        retry: false,
    });

    const fromApi = data?.status === 200 && data?.data?.dataManager === true;
    const isDataManager = !!user?.admin || fromApi;
    // While rechecking a non-granted (or unknown) result, show loading instead of a stale deny.
    const pending =
        enabled &&
        !user?.admin &&
        (isLoading || isFetching) &&
        !fromApi;

    const refreshAccess = () => {
        queryClient.invalidateQueries({ queryKey: [BDSA_ACCESS_QUERY_KEY] });
        return refetch();
    };

    return { isDataManager, pending, refreshAccess };
}
